import { Typography, TextField, Button, CircularProgress, Stack, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, FormHelperText, Checkbox } from "@mui/material";
import { CadastrarCupomForm } from "./styles";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { cupomSchema } from "../../../../schemas/cupomSchema";
import { FieldValues } from "react-hook-form/dist/types";
import { useEffect, useState, useMemo } from "react";
import { ICupom } from "../../../../../pedidos/interface/ICupom";
import { TipoCupom, TipoCupomDescricao } from "../../../../../pedidos/enum/TipoCupom";
import { TipoMarketplace, TipoMarketplaceTitulo } from "../../../../../marketplace/enum/TipoMarketplace";
import { useMarketplaces } from "../../../../../marketplace/hooks/useMarketplaces";
import { IMarketplace } from "../../../../../marketplace/interfaces/IMarketplace";
import { NumericFormat } from 'react-number-format';
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import * as yup from "yup";

interface CadastrarEditarCupomProps {
  dadosCupom?: ICupom;
  cadastrar: (cupom: FieldValues) => void;
  cancelar: () => void;
  loading: boolean;
  editar?: (cupom: FieldValues) => void;
}

type CupomFormData = yup.InferType<typeof cupomSchema>;

export const CadastrarEditarCupom = ({ cadastrar, editar, dadosCupom, cancelar, loading }: CadastrarEditarCupomProps) => {
  const { control, setValue, register, handleSubmit, watch, reset, formState: { errors } } = useForm<CupomFormData>({
    resolver: yupResolver(cupomSchema),
    mode: "onChange",
    defaultValues: {
      ativo: true,
      limiteUsoPorUsuario: null,
      marketplaces: []
    }
  });

  const { listar } = useMarketplaces();
  const [marketplacesDisponiveis, setMarketplacesDisponiveis] = useState<IMarketplace[]>([]);
  const [loadingMarketplaces, setLoadingMarketplaces] = useState(false);

  const codigoValue = watch("codigo");
  const dataValidadeValue = watch("dataValidade");
  const tipoValue = watch("tipo");

  const isPorcentagem = tipoValue === TipoCupom.PORCENTAGEM || tipoValue === TipoCupom.PORCENTAGEM_PRODUTOS;

  const { number } = useMoeda();

  const mascaraMoeda = (valor: number): string => {
    if (!valor || valor === 0) return "";
    return valor.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const aplicarMascaraMoeda = (valor: string): string => {
    if (!valor) return "";
    
    let apenasNumeros = valor.replace(/\D/g, "");
    
    if (apenasNumeros === "") return "";
    
    const valorNumerico = parseFloat(apenasNumeros) / 100;
    
    return valorNumerico.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const converterMoedaParaNumero = (valorFormatado: string): number => {
    if (!valorFormatado) return 0;
    const valorLimpo = valorFormatado.replace(/\./g, "").replace(",", ".");
    return parseFloat(valorLimpo) || 0;
  };

  const descricoesTipos: Record<TipoCupom, string> = {
    [TipoCupom.ITEM]: "Desconto aplicado em um item específico do pedido.",
    [TipoCupom.ITEM_DESCONTO_PROGRESSIVO]: "Desconto progressivo aplicado em itens específicos.",
    [TipoCupom.TAXA_ENTREGA]: "Abate o valor total da taxa de entrega do pedido.",
    [TipoCupom.TOTAL]: "Desconto aplicado no valor total do pedido.",
    [TipoCupom.PORCENTAGEM]: "Desconto em porcentagem aplicado no valor total do pedido.",
    [TipoCupom.PORCENTAGEM_PRODUTOS]: "Desconto em porcentagem aplicado apenas no valor dos produtos (não inclui taxa de entrega)."
  };

  useEffect(() => {
    let isMounted = true;
    
    const carregarMarketplaces = async () => {
      setLoadingMarketplaces(true);
      try {
        const response = await listar();
        if (!isMounted) return;
        
        const marketplaces: IMarketplace[] = response?.marketplaces || response || [];
        const marketplacesFiltrados = marketplaces.filter(
          (m) => m.tipo !== TipoMarketplace.WHATSAPP && 
                 m.tipo !== TipoMarketplace.IFOOD && 
                 m.tipo !== TipoMarketplace.RAPPI &&
                 m.tipo !== TipoMarketplace.PDV
        );
        setMarketplacesDisponiveis(marketplacesFiltrados);
      } catch (error) {
        console.error("Erro ao carregar marketplaces:", error);
      } finally {
        if (isMounted) {
          setLoadingMarketplaces(false);
        }
      }
    };
    
    carregarMarketplaces();
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  useEffect(() => {
    if(dadosCupom) {
      const { codigo, nome, descricao, tipo, valor, dataValidade, ativo, marketplaces, limiteUsoPorUsuario } = dadosCupom;
      if(codigo) setValue("codigo", codigo);
      if(nome) setValue("nome", nome);
      if(descricao) setValue("descricao", descricao);
      if(tipo && tipo !== TipoCupom.ITEM && tipo !== TipoCupom.ITEM_DESCONTO_PROGRESSIVO) {
        setValue("tipo", tipo as any);
      }
      if(valor) setValue("valor", valor);
      if(dataValidade) {
        let data: string;
        if (typeof dataValidade === 'string') {
          if (dataValidade.includes('T') && !dataValidade.includes('Z') && !dataValidade.includes('+') && !dataValidade.includes('-', 10)) {
            data = dataValidade.slice(0, 16);
          } else {
            const dateObj = new Date(dataValidade);
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const hours = String(dateObj.getHours()).padStart(2, '0');
            const minutes = String(dateObj.getMinutes()).padStart(2, '0');
            data = `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        } else {
          const dateObj = new Date(dataValidade);
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const hours = String(dateObj.getHours()).padStart(2, '0');
          const minutes = String(dateObj.getMinutes()).padStart(2, '0');
          data = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
        setValue("dataValidade", data);
      }
      if(ativo !== undefined) setValue("ativo", ativo);
      if(limiteUsoPorUsuario !== undefined && limiteUsoPorUsuario !== null) {
        setValue("limiteUsoPorUsuario", limiteUsoPorUsuario);
      }
      if(marketplaces) {
        const marketplacesFiltrados = marketplaces.map((m: { uuid?: string; tipo: TipoMarketplace }) => {
          if (m.tipo === TipoMarketplace.PDV) {
            return { tipo: TipoMarketplace.PDV, uuid: undefined };
          }
          return m;
        });
        setValue("marketplaces", marketplacesFiltrados);
      }
    }
  }, [dadosCupom, setValue])

  const handleCancelar = () => {
    reset({
      codigo: "",
      nome: "",
      descricao: "",
      tipo: undefined,
      valor: undefined,
      dataValidade: null,
      ativo: true,
      limiteUsoPorUsuario: null,
      marketplaces: []
    });
    cancelar();
  }

  const marketplacesToTipos = useMemo(() => {
    return (marketplaces: Array<{ uuid?: string; tipo: TipoMarketplace }> | null | undefined): string[] => {
      if (!marketplaces || marketplaces.length === 0) return [];
      return marketplaces.map(m => m.tipo);
    };
  }, []);

  const tiposToMarketplaces = useMemo(() => {
    return (selectedTipos: string[]): Array<{ uuid?: string; tipo: TipoMarketplace }> => {
      return selectedTipos.map((tipo: string) => {
        if (tipo === TipoMarketplace.PDV) {
          return { tipo: TipoMarketplace.PDV, uuid: undefined };
        }
        
        const marketplace = marketplacesDisponiveis.find(m => m.tipo === tipo);
        return {
          tipo: tipo as TipoMarketplace,
          uuid: marketplace?.uuid
        };
      });
    };
  }, [marketplacesDisponiveis]);

  const renderMarketplacesValue = (selected: string[]) => {
    if (selected.length === 0) return "";
    return selected.map(tipo => TipoMarketplaceTitulo[tipo as keyof typeof TipoMarketplaceTitulo] || tipo).join(", ");
  }

  const handleFormulario = async (cupom: FieldValues) => {
    console.log("Formulário submetido:", cupom);
    console.log("Erros do formulário:", errors);
    
    try {
      if(dadosCupom?.uuid && editar) {
        await editar(cupom);
      } else {
        await cadastrar(cupom);
      }
    } catch (error) {
      console.error("Erro ao processar formulário:", error);
    }
  }

  const handleSubmitWithError = handleSubmit(
    async (cupom: FieldValues) => {
      console.log("Formulário submetido com sucesso:", cupom);
      console.log("Erros do formulário:", errors);
      
      try {
        if(dadosCupom?.uuid && editar) {
          await editar(cupom);
        } else {
          await cadastrar(cupom);
        }
      } catch (error) {
        console.error("Erro ao processar formulário:", error);
      }
    },
    (errors) => {
      console.log("Erros de validação que impedem o submit:", errors);
      console.log("Valores atuais do formulário:", watch());
    }
  );

  return (
    <CadastrarCupomForm onSubmit={handleSubmitWithError}>
      <Typography variant="h6">{dadosCupom?.uuid ? "Editar cupom" : "Cadastro de cupom"}</Typography>
      <TextField
        label="Código*"
        style={{ width: "100%", margin: "0.5rem 0" }}
        size="small"
        {...register("codigo")}
        error={!!errors.codigo}
        helperText={<>{errors.codigo?.message}</>}
        autoFocus={!codigoValue}
        inputProps={{
          style: { textTransform: 'uppercase' }
        }}
        onChange={(e) => {
          const upperValue = e.target.value.toUpperCase();
          setValue("codigo", upperValue);
        }}
      />
      <TextField
        label="Nome*"
        style={{ width: "100%", margin: "0.5rem 0" }}
        size="small"
        {...register("nome")}
        error={!!errors.nome}
        helperText={<>{errors.nome?.message}</>}
      />
      <TextField
        label="Descrição*"
        style={{ width: "100%", margin: "0.5rem 0" }}
        multiline
        rows={2}
        size="small"
        {...register("descricao")}
        error={!!errors.descricao}
        helperText={<>{errors.descricao?.message}</>}
      />
      <FormControl fullWidth style={{ margin: "0.5rem 0" }} error={!!errors.tipo} size="small">
        <InputLabel>Tipo*</InputLabel>
        <Select
          {...register("tipo")}
          label="Tipo*"
          value={watch("tipo") || ""}
        >
          {Object.entries(TipoCupom)
            .filter(([key, value]) => value !== TipoCupom.ITEM_DESCONTO_PROGRESSIVO && value !== TipoCupom.ITEM)
            .map(([key, value]) => (
              <MenuItem key={value} value={value}>
                {TipoCupomDescricao[value as keyof typeof TipoCupomDescricao]}
              </MenuItem>
            ))}
        </Select>
        {errors.tipo && <FormHelperText>{errors.tipo.message as string}</FormHelperText>}
        {tipoValue && tipoValue !== TipoCupom.TAXA_ENTREGA && (
          <FormHelperText sx={{ mt: 1 }}>
            {descricoesTipos[tipoValue as TipoCupom]}
          </FormHelperText>
        )}
        {tipoValue === TipoCupom.TAXA_ENTREGA && (
          <FormHelperText sx={{ mt: 1 }}>
            {descricoesTipos[TipoCupom.TAXA_ENTREGA]}
          </FormHelperText>
        )}
      </FormControl>
      <Controller
        name="marketplaces"
        control={control}
        defaultValue={[]}
        render={({ field }) => {
          const marketplacesValue = field.value 
            ? field.value.map((m: { uuid?: string | null; tipo: TipoMarketplace }) => ({
                tipo: m.tipo,
                uuid: m.uuid === null ? undefined : m.uuid
              }))
            : undefined;
          const selectedTipos = marketplacesToTipos(marketplacesValue);
          
          return (
            <FormControl fullWidth style={{ margin: "0.5rem 0" }} error={!!errors.marketplaces} size="small">
              <InputLabel>Marketplaces</InputLabel>
              <Select
                multiple
                label="Marketplaces"
                value={selectedTipos}
                onChange={(e) => {
                  const newSelectedTipos = e.target.value as string[];
                  const newMarketplaces = tiposToMarketplaces(newSelectedTipos);
                  field.onChange(newMarketplaces);
                }}
                renderValue={renderMarketplacesValue}
                disabled={loadingMarketplaces}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                      width: 250,
                    },
                  },
                }}
              >
                <MenuItem key="PDV" value={TipoMarketplace.PDV}>
                  <Checkbox checked={selectedTipos.includes(TipoMarketplace.PDV)} />
                  {TipoMarketplaceTitulo[TipoMarketplace.PDV]}
                </MenuItem>
                {marketplacesDisponiveis.map((marketplace) => (
                  <MenuItem key={marketplace.uuid || marketplace.tipo} value={marketplace.tipo}>
                    <Checkbox checked={selectedTipos.includes(marketplace.tipo)} />
                    {TipoMarketplaceTitulo[marketplace.tipo as keyof typeof TipoMarketplaceTitulo] || marketplace.tipo}
                  </MenuItem>
                ))}
              </Select>
              {errors.marketplaces && <FormHelperText>{errors.marketplaces.message as string}</FormHelperText>}
            </FormControl>
          );
        }}
      />
      {tipoValue === TipoCupom.TAXA_ENTREGA ? null : (
        isPorcentagem ? (
          <Controller
            name="valor"
            control={control}
            render={({ field }) => (
              <NumericFormat
                customInput={TextField}
                label="Valor*"
                style={{ width: "100%", margin: "0.5rem 0" }}
                size="small"
                value={field.value || ""}
                onValueChange={(values) => {
                  const numValue = values.floatValue;
                  field.onChange(numValue !== undefined ? numValue : "");
                }}
                error={!!errors.valor}
                helperText={errors.valor?.message as string}
                suffix="%"
                decimalScale={2}
                allowNegative={false}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
                }}
              />
            )}
          />
        ) : (
          <Controller
            name="valor"
            control={control}
            render={({ field }) => {
              const valorFormatado = field.value 
                ? `R$ ${mascaraMoeda(field.value)}`
                : "";
              
              return (
                <TextField
                  label="Valor*"
                  style={{ width: "100%", margin: "0.5rem 0" }}
                  size="small"
                  value={valorFormatado}
                  onChange={(e) => {
                    const valorDigitado = e.target.value.replace("R$", "").trim();
                    const valorFormatado = aplicarMascaraMoeda(valorDigitado);
                    const valorNumerico = converterMoedaParaNumero(valorFormatado);
                    field.onChange(valorNumerico);
                  }}
                  error={!!errors.valor}
                  helperText={errors.valor?.message as string}
                  placeholder="R$ 0,00"
                />
              );
            }}
          />
        )
      )}
      <Controller
        name="limiteUsoPorUsuario"
        control={control}
        render={({ field }) => (
          <TextField
            label="Limite de uso por usuário"
            type="number"
            style={{ width: "100%", margin: "0.5rem 0" }}
            size="small"
            value={field.value ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              field.onChange(value === "" ? null : Number(value));
            }}
            error={!!errors.limiteUsoPorUsuario}
            helperText={(errors.limiteUsoPorUsuario?.message as string) || "Opcional. Deixe em branco para uso ilimitado."}
            inputProps={{ min: 1, step: 1 }}
          />
        )}
      />
      <Controller
        name="dataValidade"
        control={control}
        render={({ field }) => (
          <TextField
            label="Data de Validade"
            type="datetime-local"
            style={{ width: "100%", margin: "0.5rem 0" }}
            size="small"
            value={field.value || ""}
            onChange={(e) => {
              field.onChange(e.target.value === "" ? null : e.target.value);
            }}
            error={!!errors.dataValidade}
            helperText={errors.dataValidade?.message as string}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
      />
      <FormControlLabel
        control={
          <Switch
            checked={watch("ativo") ?? true}
            onChange={(e) => setValue("ativo", e.target.checked)}
          />
        }
        label="Ativo"
        style={{ margin: "0.5rem 0" }}
      />
      <Stack flexDirection={"row"} sx={{ width: "100%" }} justifyContent={"space-between"}>
        <Button
          variant="outlined"
          style={{ width: "47%" }}
          type="button"
          onClick={handleCancelar}
        >
          CANCELAR
        </Button>
        <Button
          variant="contained"
          style={{ width: "47%" }}
          type="submit"
          disabled={loading}
          onClick={(e) => {
            console.log("Botão clicado");
            console.log("Valores do formulário:", watch());
            console.log("Erros:", errors);
          }}
        >
          {loading == true ? <CircularProgress color="inherit" /> : (
            dadosCupom?.uuid ? "EDITAR" : "CADASTRAR"
          )}
        </Button>
      </Stack>
    </CadastrarCupomForm>
  );
};
